import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { FormikProps } from 'formik';
import * as _ from 'lodash';
import { useEffect } from 'react';
import { CommonTextField } from 'gemlux-common-components';
import { Box, Link, SelectChangeEvent } from '@mui/material';
import { theme } from '../../../theme/theme';
import { countries, mapDesignations } from '../../../utils/sign-up-utils';
import CustomCheckbox from '../../CustomCheckbox/CustomCheckbox';
import { ICredentials } from '../../../redux/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import { userActions } from '../../../redux/user/userSlice';
import RoleDropdown from '../../RoleDropdown/RoleDropdown';
import WarningIcon from '../../../assets/img/warning-red.svg';
import CustomDropdown from '../../V1/CustomDropdown/CustomDropdown';

type IBusinessCredentialProps = {
  formik: FormikProps<ICredentials>;
};

function BusinessCredential({ formik }: IBusinessCredentialProps) {
  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const error = _.get(componentStatus, 'error', undefined) as string;
  const designations = useAppSelector((state: RootState) => state.user.designations) || [];
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (designations.length === 0) {
      dispatch(userActions.getDesignationsAsync());
    }
  }, []);

  const handleRoleChange = (event: SelectChangeEvent<unknown>, otherValue?: string) => {
    const value = event.target.value as string;
    formik.setFieldValue('designationId', value);

    if (value === '7') {
      formik.setFieldValue('customDesignation', otherValue || '');
    } else {
      formik.setFieldValue('customDesignation', '');
    }
  };

  return (
    <>
      <Grid container spacing="20px">
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="company-name">
            Company Name
          </Typography>
          <CommonTextField
            id="company-name"
            type="text"
            name="companyName"
            value={formik.values.companyName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyName && Boolean(formik.errors.companyName)}
            helperText={formik.touched.companyName && formik.errors.companyName}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorBorderColor="#DF0C3D"
            errorTextColor="#DF0C3D"
            sx={{ marginTop: '4px' }}
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" id="country-label">
            Country
          </Typography>
          <CustomDropdown
            ariaLabelledBy="country-label"
            options={countries}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.country}
            name="country"
            error={formik.touched.country ? formik.errors.country : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" id="designationId-label">
            Your Role in the Company
          </Typography>
          <RoleDropdown
            ariaLabelledBy="designationId-label"
            options={mapDesignations(designations)}
            onChange={handleRoleChange}
            onBlur={formik.handleBlur}
            value={formik.values.designationId}
            name="designationId"
            error={formik.touched.designationId ? formik.errors.designationId : undefined}
            errorOther={formik.touched.customDesignation ? formik.errors.customDesignation : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="textBase500" component="label" htmlFor="company-website">
            Company Website
          </Typography>
          <CommonTextField
            id="company-website"
            type="url"
            name="companyWebsite"
            value={formik.values.companyWebsite}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.companyWebsite && Boolean(formik.errors.companyWebsite)}
            helperText={formik.touched.companyWebsite && formik.errors.companyWebsite}
            variant="outlined"
            FormHelperTextProps={{ style: { ...theme.typography.meta, marginLeft: 0 } }}
            errorBorderColor="#DF0C3D"
            errorTextColor="#DF0C3D"
            sx={{ marginTop: '4px' }}
          />
        </Grid>
      </Grid>
      <Grid display="flex" alignItems="center" gap={0.5}>
        <CustomCheckbox
          onClick={() =>
            formik.setFieldValue('isAgreedToTermsAndCondition', !formik.values.isAgreedToTermsAndCondition)
          }
          checked={formik.values.isAgreedToTermsAndCondition}
        />
        <Typography variant="tableRowRegular">I confirm that I have read and agree to</Typography>
        <Link href="/terms&conditions" color="#3348FF" underline="always" target="_blank" rel="noopener noreferrer">
          <Typography variant="tableRowRegular" color="secondary">
            Porolis’ Terms and Condition of Use
          </Typography>
        </Link>
      </Grid>
      {formik.touched.isAgreedToTermsAndCondition && formik.errors.isAgreedToTermsAndCondition && (
        <Typography variant="textSM" color="#DF0C3D" textAlign="left" component="div">
          <Box display="flex" alignItems="center" gap="4px">
            <Box component="img" src={WarningIcon} width={18} height={18} />
            {formik.errors.isAgreedToTermsAndCondition}
          </Box>
        </Typography>
      )}
      {error && (
        <Typography variant="textSM" color="#DF0C3D" textAlign="center">
          {_.capitalize(error)}
        </Typography>
      )}
    </>
  );
}

export default BusinessCredential;
